import type { WireByCellKey } from 'store/useCellAnnotationStore/types';
import { parseCellKey } from '../types';
import type { CellAnnotationWireEntry, CellAnnotationWireMap } from '../types';

// Groups dirty cell-annotation entries by parent scope so callers can fold
// each group into the right nested payload at save time.
//
// Output shape per parent: { field_alias: { annotation_type: entry } } —
// matches the backend writable cell_annotations field directly. Callers
// drop this map onto the parent body and the backend routes each
// (field_alias, annotation_type) to the matching CellAnnotation row.
export interface DistributedPayload {
  capTableByParentId: Record<string, CellAnnotationWireMap>;
  fundOwnershipDetailByParentId: Record<string, CellAnnotationWireMap>;
}

const foldEntry = (
  bucket: Record<string, CellAnnotationWireMap>,
  parentId: string,
  fieldAlias: string,
  annotationType: string,
  entry: CellAnnotationWireEntry
): void => {
  // eslint-disable-next-line no-param-reassign
  const forParent = bucket[parentId] ?? (bucket[parentId] = {});
  const forField = forParent[fieldAlias] ?? (forParent[fieldAlias] = {});
  // The annotationType segment came from a cellKey we just parsed against the
  // CellAnnotationType union — coerce so consumers see the typed wire shape.
  forField[annotationType as keyof typeof forField] = entry;
};

export const distributeDirtyPayload = (payload: WireByCellKey): DistributedPayload => {
  const capTableByParentId: Record<string, CellAnnotationWireMap> = {};
  const fundOwnershipDetailByParentId: Record<string, CellAnnotationWireMap> = {};

  Object.entries(payload).forEach(([cellKey, entry]) => {
    const parsed = parseCellKey(cellKey);
    if (!parsed) {
      return;
    }
    const { parentType, parentId, fieldAlias, annotationType } = parsed;

    if (parentType === 'captable') {
      foldEntry(capTableByParentId, parentId, fieldAlias, annotationType, entry as CellAnnotationWireEntry);
    } else if (parentType === 'fundownershipdetail') {
      foldEntry(
        fundOwnershipDetailByParentId,
        parentId,
        fieldAlias,
        annotationType,
        entry as CellAnnotationWireEntry
      );
    }
    // Unknown parentType: silently skip. Callers should never be asked to
    // route entries they don't know how to fold.
  });

  return { capTableByParentId, fundOwnershipDetailByParentId };
};
